import { useState, useMemo } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getOrderedCountries, getCountryByDialCode, type CountryData } from '@/lib/countries-data';

// CDN para bandeiras em alta qualidade (flag-icons por lipis)
const getFlagUrl = (countryCode: string) => 
  `https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/flags/4x3/${countryCode.toLowerCase()}.svg`;

interface CountryPhoneSelectorProps {
  value: string; // dialCode atual (ex: "+1")
  onChange: (dialCode: string, countryCode?: string) => void; // Agora também retorna o código do país
  className?: string;
  disabled?: boolean;
  countryCode?: string; // Código do país preferido (ex: "US" ou "CA")
}

export function CountryPhoneSelector({ value, onChange, className, disabled = false, countryCode }: CountryPhoneSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const countries = useMemo(() => getOrderedCountries(), []);
  const selectedCountry = useMemo(() => {
    // Tenta obter o país preferido, ou usa o countryCode prop, ou busca do localStorage
    return getCountryByDialCode(value, countryCode) || countries[0];
  }, [value, countries, countryCode]);

  // Filtra países baseado na busca
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return countries;
    
    const lowerQuery = searchQuery.toLowerCase();
    return countries.filter(country => 
      country.name.toLowerCase().includes(lowerQuery) ||
      country.dialCode.includes(searchQuery) ||
      country.code.toLowerCase().includes(lowerQuery)
    );
  }, [searchQuery, countries]);

  const handleSelect = (country: CountryData) => {
    // Memoriza a seleção do usuário no localStorage para países com mesmo dialCode
    try {
      const storageKey = `lastSelectedCountry_${country.dialCode}`;
      localStorage.setItem(storageKey, country.code);
    } catch (error) {
      // Se localStorage não estiver disponível, ignora
    }
    
    onChange(country.dialCode, country.code);
    setOpen(false);
    setSearchQuery('');
  };

  // Componente para item da lista (usado tanto no desktop quanto mobile)
  const CountryItem = ({ country, isSelected }: { country: CountryData; isSelected: boolean }) => (
    <div
      onClick={() => handleSelect(country)}
      className={`
        flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-all duration-150
        ${isSelected 
          ? 'bg-primary/5 text-primary' 
          : 'hover:bg-muted/50 active:bg-muted'}
      `}
    >
      <img 
        src={getFlagUrl(country.code)} 
        alt={country.name}
        className="w-6 h-4 object-cover flex-shrink-0 border border-border/50"
        style={{ borderRadius: '5px' }}
        loading="lazy"
      />
      <span className={`flex-1 text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
        {country.name}
      </span>
      <span className={`text-xs font-mono ${isSelected ? 'text-primary/70' : 'text-muted-foreground'}`}>
        {country.dialCode}
      </span>
      {isSelected && (
        <Check className="h-4 w-4 text-primary flex-shrink-0" />
      )}
    </div>
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={`justify-between w-24 ${className || ''} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
      >
        <span className="flex items-center gap-1.5">
          <img 
            src={getFlagUrl(selectedCountry.code)} 
            alt={selectedCountry.name}
            className="w-5 h-3.5 object-cover border border-border/50"
            style={{ borderRadius: '5px' }}
            loading="lazy"
          />
          <span className="text-xs font-mono">
            {selectedCountry.code}
          </span>
        </span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 max-w-md">
          <DialogHeader className="px-5 pt-5 pb-4 border-b">
            <DialogTitle className="text-lg font-semibold">Select your country</DialogTitle>
          </DialogHeader>
          
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search for a country..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="h-11 border-0"
            />
            
            <CommandList className="max-h-[400px]">
              {filteredCountries.length === 0 && searchQuery.trim() ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No countries found.
                </div>
              ) : (
                <CommandGroup>
                  <div className="divide-y">
                    {filteredCountries.map((country) => (
                      <CountryItem
                        key={country.code}
                        country={country}
                        isSelected={country.code === selectedCountry.code && country.dialCode === value}
                      />
                    ))}
                  </div>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
